import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ContentProvider } from "@/context/ContentContext";

import SiteLayout from "@/layouts/SiteLayout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import WhyChoose from "@/pages/WhyChoose";
import HowItWorks from "@/pages/HowItWorks";
import Contact from "@/pages/Contact";
import Urgent from "@/pages/Urgent";
import Care from "@/pages/Care";
import Hub from "@/pages/Hub";
import Condition from "@/pages/Condition";
import NRI from "@/pages/NRI";
import Country from "@/pages/Country";
import Cities from "@/pages/Cities";
import City from "@/pages/City";
import Book from "@/pages/Book";
import SecondOpinion from "@/pages/SecondOpinion";
import Journal from "@/pages/Journal";
import JournalArticle from "@/pages/JournalArticle";
import NotFound from "@/pages/NotFound";

import AdminLayout from "@/layouts/AdminLayout";
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminLeads from "@/pages/admin/Leads";
import AdminContent from "@/pages/admin/Content";
import AdminJournal from "@/pages/admin/Journal";
import RequireAuth from "@/components/RequireAuth";
import AnalyticsProvider from "@/components/AnalyticsProvider";

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
      wheelMultiplier: 1.0,
    });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
}

function RootShell() {
  useLenis();
  return (
    <>
      <div className="grain" aria-hidden="true" />
      <Outlet />
      <Toaster position="bottom-right" />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ContentProvider>
        <AuthProvider>
          <BrowserRouter>
            <AnalyticsProvider>
              <Routes>
              <Route element={<RootShell />}>
                <Route element={<SiteLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/why-choose" element={<WhyChoose />} />
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/urgent" element={<Urgent />} />
                  <Route path="/care" element={<Care />} />
                  <Route path="/care/:slug" element={<Hub />} />
                  <Route path="/conditions/:slug" element={<Condition />} />
                  <Route path="/nri" element={<NRI />} />
                  <Route path="/nri/:country" element={<Country />} />
                  <Route path="/cities" element={<Cities />} />
                  <Route path="/cities/:slug" element={<City />} />
                  <Route path="/book" element={<Book />} />
                  <Route path="/second-opinion" element={<SecondOpinion />} />
                  <Route path="/journal" element={<Journal />} />
                  <Route path="/journal/:slug" element={<JournalArticle />} />
                </Route>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <RequireAuth>
                      <AdminLayout />
                    </RequireAuth>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="leads" element={<AdminLeads />} />
                  <Route path="content" element={<AdminContent />} />
                  <Route path="journal" element={<AdminJournal />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Route>
              </Routes>
            </AnalyticsProvider>
          </BrowserRouter>
        </AuthProvider>
      </ContentProvider>
    </div>
  );
}

export default App;
